import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useAllSearchParams, useSearch } from '~libs/searchParams';

import { ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import columnsFactory from '~support-at-home/forms/sah-associations/list';
import { createSearchFields } from '~support-at-home/forms/sah-associations/search';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { setTitle } from '~main/actions/login';
import {
  useGetAssignableServiceTypesQuery,
  useGetAssocsQuery,
  useUpdateAssocMutation,
} from '~support-at-home/actions/sahServiceTypeAssociations';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

const SahServiceAssociations: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);

  const searchParamValues = useAllSearchParams();
  const { sah_service_id, service_type_id } = searchParamValues;
  const initialSearchState: any = {
    sah_service_id: sah_service_id ?? undefined,
    service_type_id: service_type_id ?? undefined,
  };

  const [searchParams, setSearchParams] = useSearch('search', initialSearchState);
  const [params, setParams] = useState({ limit: 30 });

  // RTK Queries
  const { data: sahServicesData = [] } = useGetSahServicesQuery({}, { skip: !isActive });
  const { data: assignableServiceTypes = [] } = useGetAssignableServiceTypesQuery({}, { skip: !isActive });
  const { data: assocsData, isLoading } = useGetAssocsQuery(
    { ...params, ...searchParams },
    { skip: !isActive, refetchOnMountOrArgChange: true },
  );
  const [updateAssoc] = useUpdateAssocMutation();

  const searchFields = createSearchFields(sahServicesData);
  const [data, setData] = useState<Row[]>(assocsData?.rows ?? []);

  useEffect(() => {
    if (isActive) {
      setTitle('SaH Service Associations');
      dispatch(getContributionCategories({ sort: ['sequence'] }));
      dispatch(getServiceTypes());
    }
  }, [isActive, dispatch]);

  useEffect(() => {
    setData(assocsData?.rows ?? []);
  }, [assocsData]);

  const load = (loadParams: { [key: string]: any } = {}) => {
    setParams((prev) => ({ ...prev, ...loadParams }));
  };

  const handleUpdateRow = async (row: Row) => {
    const { id, service_type_ids } = row;
    await updateAssoc({ sah_service_id: id, service_type_ids });
  };

  const handleChange = (rows: Row[]) => {
    setData(rows);
  };

  const columns = columnsFactory(serviceTypes, new Set(assignableServiceTypes.map((type) => type.id)));

  const search = (values: { [key: string]: any }) => setSearchParams(values);

  return (
    <div className="p-2">
      <div>
        <Toolbar
          id="service-associations"
          rightCluster={
            <ClickableIcon
              id="sah-client-contributions-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
          }
        />
        <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
        <TableView
          id={'sah_service_assocs_table'}
          data={data}
          columns={columns}
          emptyMessage={'No SAH Service Types found'}
          allowPagination={true}
          totalCount={assocsData?.total ?? 0}
          onLoadMore={() => load({ limit: params.limit + 30 })}
          canDeleteRow={false}
          onSaveRow={handleUpdateRow}
          onChange={handleChange}
          isLoading={isLoading}
          showAddRow={false}
        />
      </div>
    </div>
  );
};

export default SahServiceAssociations;
